import React, { useState, useEffect } from 'react';
import SalesDetailPage from './SalesDetailPage';
import { OFFERS_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/offersColumnsConfig';
import { compactCurrency } from '../utils/reportingUtils';
import { buildOffersFilterGroups } from '../constants/reportingOffersFiltersConfig';

const axios = require('axios').default;

const STATE_BADGES = {
  draft: { bg: 'var(--sl-bg-tertiary, #f2f4f7)', color: 'var(--sl-text-secondary, #344054)' },
  sent: { bg: 'var(--sl-bg-brand-primary, #eff8ff)', color: 'var(--sl-fg-brand, #175cd3)' },
  accepted: { bg: 'var(--sl-bg-success-primary, #ecfdf3)', color: 'var(--sl-text-success, #027a48)' },
  declined: { bg: 'var(--sl-bg-error-primary, #fef3f2)', color: 'var(--sl-text-error, #b42318)' },
};

const ACT_TYPE_LABELS = {
  headline: 'Headliner',
  support: 'Support',
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_id && activeFilters.venue_id.length > 0) {
    params.venue_id = activeFilters.venue_id;
  }
  if (activeFilters.state && activeFilters.state.length > 0) {
    params.state = activeFilters.state;
  }
  if (activeFilters.owner && activeFilters.owner.length > 0) {
    params.owner = activeFilters.owner;
  }
  if (activeFilters.buyer && activeFilters.buyer.length > 0) {
    params.buyer = activeFilters.buyer;
  }
  if (activeFilters.act_type && activeFilters.act_type.length > 0) {
    params.act_type = activeFilters.act_type;
  }
  if (activeFilters.has_event && activeFilters.has_event.length > 0) {
    params.has_event = activeFilters.has_event;
  }
  if (activeFilters.has_holds && activeFilters.has_holds.length > 0) {
    params.has_holds = activeFilters.has_holds;
  }
  if (activeFilters.genre && activeFilters.genre.length > 0) {
    params.genre = activeFilters.genre;
  }
  return params;
};

const OFFERS_CONFIG = {
  pageTitle: 'Offers',
  pageSubtitle: 'Offer activity across your venues.',
  sidebarKey: 'booking_offers',
  defaultPreset: 'year_to_date',
  dateModeOptions: [
    { key: 'order_date', label: 'Created Date' },
    { key: 'event_date', label: 'Event Date' },
  ],

  columns: OFFERS_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingBookingOfferColumns_${teamId}`,
  firstColumnId: 'event_name',

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/booking/offers/data.json`,
  detailResponseKey: 'offers',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/booking/offers/export`,
  salesExportSection: 'offers',
  detailErrorMessage: 'Failed to load offer data.',
  detailTableTitle: 'Offers',
  emptyMessage: 'No offers found.',
  searchPlaceholder: 'Search offers...',
  paginationLabel: 'offers',
  itemLabel: (count) => `offer${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,

  buildKpiCards: (current, prior) => [
    { label: 'Total Offers', value: (current.total_offers || 0).toLocaleString(), current: current.total_offers || 0, prior: prior.total_offers || 0 },
    { label: 'Accepted', value: (current.accepted || 0).toLocaleString(), current: current.accepted || 0, prior: prior.accepted || 0 },
    { label: 'Acceptance Rate', value: `${current.acceptance_rate || 0}%`, current: current.acceptance_rate || 0, prior: prior.acceptance_rate || 0 },
    { label: 'Total Amount', value: compactCurrency(current.total_amount || 0), current: current.total_amount || 0, prior: prior.total_amount || 0 },
  ],
  kpiGridColumns: 4,

  chartColor: 'var(--sl-fg-brand, #1982C4)',
  salesCardIsCurrency: false,
  salesCardTitle: 'Offers',
  buildMetricValue: (d) => (d.total_offers || 0).toLocaleString(),
  buildMetricLabel: (d) => `${(d.accepted || 0).toLocaleString()} accepted`,
  overTimeKey: 'over_time',
  countLabel: 'offers',

  topCharts: [
    { title: 'Offers by Venue', dataKey: 'by_venue', barDataKey: 'count', nameKey: 'name', barColor: 'var(--sl-fg-brand, #1982C4)', isCurrency: false, tooltipLabel: 'Offers' },
    { title: 'Offers by Buyer', dataKey: 'by_buyer', barDataKey: 'count', nameKey: 'name', barColor: '#6A4C93', isCurrency: false, tooltipLabel: 'Offers' },
  ],

  groupByOptions: [
    { key: 'venue', label: 'Venue' },
    { key: 'offer', label: 'Individual Offer' },
  ],
  groupByDefault: 'offer',
  eventGroupedColumns: [
    { key: 'venue_name', label: 'Venue' },
    { key: 'offer_count', label: 'Offers' },
    { key: 'total_amount', label: 'Total Amount' },
    { key: 'accepted_count', label: 'Accepted' },
  ],

  renderEventCell: (key, row, { currencyFormatter: cf }) => {
    switch (key) {
      case 'venue_name': return row.venue_name || '\u2014';
      case 'offer_count': return row.offer_count.toLocaleString();
      case 'total_amount': return cf.format(row.total_amount || 0);
      case 'accepted_count': return row.accepted_count.toLocaleString();
      default: return '\u2014';
    }
  },

  renderCellValue: (col, offer, { formatEventDate, formatEventDateTime, currencyFormatter: cf }) => {
    switch (col.id) {
      case 'event_name': return offer.event_name || '\u2014';
      case 'venue_name': return offer.venue_name || '\u2014';
      case 'artist_name': return offer.artist_name || '\u2014';
      case 'dollar_amount': return cf.format(offer.dollar_amount || 0);
      case 'state': {
        const badge = STATE_BADGES[offer.state] || STATE_BADGES.draft;
        const label = offer.state ? offer.state.charAt(0).toUpperCase() + offer.state.slice(1) : '\u2014';
        return <span className="badge" style={{ background: badge.bg, color: badge.color, fontSize: 12 }}>{label}</span>;
      }
      case 'act_type': return ACT_TYPE_LABELS[offer.act_type] || '\u2014';
      case 'created_at': return <span className="text-nowrap">{formatEventDateTime(offer.created_at)}</span>;
      case 'owner_name': return offer.owner_name || '\u2014';
      case 'buyer_name': return offer.buyer_name || '\u2014';
      case 'start_date': return <span className="text-nowrap">{offer.start_date ? formatEventDate(offer.start_date) : '\u2014'}</span>;
      case 'has_event':
        return offer.has_event
          ? <span className="badge" style={{ background: 'var(--sl-bg-success-primary, #ecfdf3)', color: 'var(--sl-text-success, #027a48)', fontSize: 12 }}>Yes</span>
          : <span className="badge" style={{ background: 'var(--sl-bg-tertiary, #f2f4f7)', color: 'var(--sl-text-secondary, #344054)', fontSize: 12 }}>No</span>;
      case 'has_holds':
        return offer.has_holds
          ? <span className="badge" style={{ background: 'var(--sl-bg-success-primary, #ecfdf3)', color: 'var(--sl-text-success, #027a48)', fontSize: 12 }}>Yes</span>
          : <span className="badge" style={{ background: 'var(--sl-bg-tertiary, #f2f4f7)', color: 'var(--sl-text-secondary, #344054)', fontSize: 12 }}>No</span>;
      case 'genres': return (offer.genres || []).join(', ') || '\u2014';
      case 'expires_at': return <span className="text-nowrap">{offer.expires_at ? formatEventDateTime(offer.expires_at) : '\u2014'}</span>;
      default: return '\u2014';
    }
  },

  filterTitle: 'Filter Offers',
  filterSubtitle: 'Apply filters to narrow offer data.',
};

function ReportingBookingOffersPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/booking/offers/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch(() => {});
  }, [team && team.id, csrfToken]);

  const configWithFilters = {
    ...OFFERS_CONFIG,
    buildFilterGroups: () => buildOffersFilterGroups(filterOptions),
  };
  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithFilters} />;
}

export default ReportingBookingOffersPage;
